// Utilitários para gerar diferentes tipos de QR Code

/**
 * Gera vCard no formato padrão
 * @param {Object} data - Dados do contato
 * @returns {string} vCard formatado
 */
export const generateVCard = (data) => {
  const {
    name = '',
    organization = '',
    title = '',
    phone = '',
    email = '',
    website = '',
    address = '',
    note = ''
  } = data;

  let vcard = 'BEGIN:VCARD\n';
  vcard += 'VERSION:3.0\n';
  
  if (name) {
    vcard += `FN:${name}\n`;
    vcard += `N:${name};;;;\n`;
  }
  
  if (organization) {
    vcard += `ORG:${organization}\n`;
  }
  
  if (title) {
    vcard += `TITLE:${title}\n`;
  }
  
  if (phone) {
    vcard += `TEL:${phone}\n`;
  }
  
  if (email) {
    vcard += `EMAIL:${email}\n`;
  }
  
  if (website) {
    vcard += `URL:${website}\n`;
  }
  
  if (address) {
    // vCard espera endereço em formato específico
    const addressLines = address.split('\n').filter(line => line.trim());
    if (addressLines.length > 0) {
      vcard += `ADR:;;${addressLines.join(';')};;;;\n`;
    }
  }
  
  if (note) {
    vcard += `NOTE:${note}\n`;
  }
  
  vcard += 'END:VCARD';
  
  return vcard;
};

/**
 * Gera string WiFi QR Code no formato padrão
 * @param {Object} data - Dados da rede WiFi
 * @returns {string} String WiFi formatada
 */
export const generateWiFiString = (data) => {
  const {
    ssid = '',
    password = '',
    security = 'WPA',
    hidden = false
  } = data;

  if (!ssid.trim()) {
    return '';
  }

  let wifiString = `WIFI:T:${security};S:${ssid};`;
  
  if (security !== 'nopass' && password) {
    wifiString += `P:${password};`;
  }
  
  if (hidden) {
    wifiString += 'H:true;';
  }
  
  wifiString += ';'; // Fecha a string
  
  return wifiString;
};

/**
 * Valida URL
 * @param {string} url - URL para validar
 * @returns {boolean} True se válida
 */
export const isValidURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Gera o texto final baseado no tipo de QR Code
 * @param {string} type - Tipo do QR Code
 * @param {string|Object} data - Dados do QR Code
 * @returns {string} Texto final para gerar o QR Code
 */
export const generateQRText = (type, data) => {
  switch (type) {
    case 'url':
      return typeof data === 'string' ? data : '';
    
    case 'text':
      return typeof data === 'string' ? data : '';
    
    case 'vcard':
      return typeof data === 'object' ? generateVCard(data) : '';
    
    case 'wifi':
      return typeof data === 'object' ? generateWiFiString(data) : '';
    
    default:
      return '';
  }
};

/**
 * Valida dados baseado no tipo de QR Code
 * @param {string} type - Tipo do QR Code
 * @param {string|Object} data - Dados para validar
 * @returns {Object} { isValid: boolean, message: string }
 */
export const validateQRData = (type, data) => {
  switch (type) {
    case 'url':
      if (!data || typeof data !== 'string') {
        return { isValid: false, message: 'URL é obrigatória' };
      }
      if (!isValidURL(data)) {
        return { isValid: false, message: 'URL inválida. Use http:// ou https://' };
      }
      return { isValid: true, message: 'URL válida' };
    
    case 'text':
      if (!data || typeof data !== 'string' || data.trim().length === 0) {
        return { isValid: false, message: 'Texto é obrigatório' };
      }
      if (data.length > 2953) {
        return { isValid: false, message: 'Texto muito longo (máximo 2953 caracteres)' };
      }
      return { isValid: true, message: 'Texto válido' };
    
    case 'vcard':
      if (!data || typeof data !== 'object') {
        return { isValid: false, message: 'Dados do contato são obrigatórios' };
      }
      if (!data.name || data.name.trim().length === 0) {
        return { isValid: false, message: 'Nome é obrigatório' };
      }
      return { isValid: true, message: 'Contato válido' };
    
    case 'wifi':
      if (!data || typeof data !== 'object') {
        return { isValid: false, message: 'Dados da rede WiFi são obrigatórios' };
      }
      if (!data.ssid || data.ssid.trim().length === 0) {
        return { isValid: false, message: 'Nome da rede (SSID) é obrigatório' };
      }
      if (data.security !== 'nopass' && (!data.password || data.password.length === 0)) {
        return { isValid: false, message: 'Senha é obrigatória para redes protegidas' };
      }
      return { isValid: true, message: 'Rede WiFi válida' };
    
    default:
      return { isValid: false, message: 'Tipo de QR Code inválido' };
  }
};
